import type { TFunction } from 'i18next'

import type { ViolationSection } from '~/components/bidding/common/SubmitConfirmModal'

export type IntradayApprovalWarnings = {
  /** 承認確認モーダルの `warningSections` (承認者非該当 / 自己承認、 ブロックしない)。 */
  approvalWarningSections: ViolationSection[]
  /**
   * 差戻しダイアログの `description`。 `RejectDialog` に専用の警告枠が無いため、
   * 通常の説明文に警告文を連結する。
   */
  rejectDescription: string
}

/**
 * 承認者非該当 / 自己承認 (AC-BIN-098) の確認モーダル用の文言を組み立てる。
 * 詳細モーダルと入札一覧の承認 hook が同じ文言 ・ 同じ構成を使う。
 * 警告は承認 ・ 差戻しを止めない (AC-BIN-098)。 売買逆転 (AC-BIN-097) は承認確認だけに出す
 * (差戻しは逆転を解消する操作なので警告しない)。
 */
export const buildIntradayApprovalWarnings = (
  t: TFunction,
  input: {
    isNotApprover: boolean
    isSelfApplicant: boolean
    approverListLabel: string
    /** 売買逆転の警告 (AC-BIN-097、 承認をブロックしない)。 該当が無ければ undefined。 */
    inversionSection?: ViolationSection
  },
): IntradayApprovalWarnings => {
  const {
    isNotApprover,
    isSelfApplicant,
    approverListLabel,
    inversionSection,
  } = input
  const approvalWarningSections: ViolationSection[] = [
    ...(isNotApprover
      ? [
          {
            title: t(
              'page:biddingIntraday.approvalAction.notApproverConfirmTitle',
            ),
            body: t(
              'page:biddingIntraday.approvalAction.notApproverConfirmApproveBody',
            ),
            details: [
              t(
                'page:biddingIntraday.approvalAction.notApproverConfirmDetail',
                { approvers: approverListLabel },
              ),
            ],
          },
        ]
      : []),
    ...(isSelfApplicant
      ? [
          {
            title: t(
              'page:biddingIntraday.approvalAction.selfApprovalConfirmTitle',
            ),
            body: t(
              'page:biddingIntraday.approvalAction.selfApprovalConfirmApproveBody',
            ),
            details: [
              t(
                'page:biddingIntraday.approvalAction.selfApprovalConfirmDetail',
              ),
            ],
          },
        ]
      : []),
    ...(inversionSection !== undefined ? [inversionSection] : []),
  ]
  const rejectWarningTexts = [
    isNotApprover
      ? t('page:biddingIntraday.approvalAction.notApproverConfirmRejectBody', {
          approvers: approverListLabel,
        })
      : undefined,
    isSelfApplicant
      ? t('page:biddingIntraday.approvalAction.selfApprovalConfirmRejectBody')
      : undefined,
  ].filter((text): text is string => text !== undefined)
  const rejectDescription = [
    t('page:biddingIntraday.approvalAction.rejectDescription'),
    ...rejectWarningTexts,
  ].join(' ')
  return { approvalWarningSections, rejectDescription }
}
